<template>
  <div class="discrete-query-wizard">
    <nav class="wizard-nav" aria-label="Query workflow">
      <v-btn
        v-for="step in stepNavItems"
        :key="step.id"
        flat
        :disabled="!canNavigateTo(step.id)"
        :class="{ 'wizard-nav-current': activeStep === step.id, 'wizard-nav-done': isStepComplete(step.id) }"
        @click="goToStep(step.id)"
      >
        <span class="wizard-nav-index">{{ isStepComplete(step.id) ? '✓' : step.id }}</span>
        <v-icon small>{{ step.icon }}</v-icon>
        <span class="wizard-nav-label">{{ step.label }}</span>
      </v-btn>
    </nav>
    <div class="state-banner" :class="{ 'state-banner-pending': !canSubmit }" role="status" aria-live="polite">
      <span class="state-mark">{{ canSubmit ? '✓' : '!' }}</span>
      <div>
        <div class="state-title">{{ stateBannerTitle }}</div>
        <div class="state-copy">{{ stateBannerCopy }}</div>
      </div>
    </div>

    <v-alert v-if="initializationError" :value="true" type="error" outline>
      {{ initializationError }}
    </v-alert>
    <v-alert v-if="errorMessage" :value="true" type="error" outline dismissible @input="errorMessage = ''">
      {{ errorMessage }}
    </v-alert>
    <v-alert v-if="isDirty" :value="true" type="warning" outline>
      {{ statusMessage }}
    </v-alert>

    <v-card id="query-step-1" class="wizard-step" :class="{ 'step-completed': steps.scope }" flat>
      <div class="step-rail">
        <span class="step-number" :class="{ 'step-number-done': steps.scope }">1</span>
      </div>
      <div class="wizard-step-main">
      <v-card-title class="headline">
        <v-icon class="step-heading-icon">place</v-icon>
        Set the data scope
        <span class="step-status">{{ steps.scope ? 'Required · applied' : 'Required' }}</span>
      </v-card-title>
      <v-card-text>
        <p class="wizard-description">Media determines which locations, analytes, and guidelines are available.</p>
        <div class="scope-fields">
          <section class="scope-field media-field" aria-labelledby="media-types-label">
            <div id="media-types-label" class="field-label">
              <v-icon small>filter_list</v-icon>
              <span>1. Media type</span>
            </div>
            <v-select
              ref="mediaSelect"
              v-model="draft.mediaTypes"
              :items="allMediaTypes"
              :disabled="!isInitialized || busy"
              :menu-props="{ maxHeight: 320 }"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              hide-details
              class="media-select"
              placeholder="Select one or more media types"
              prepend-inner-icon="filter_list"
              aria-labelledby="media-types-label"
              @change="onMediaTypesChange"
            >
              <template v-slot:no-data>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ isInitialized ? 'No media types are available.' : 'Loading media types…' }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>
            </v-select>
            <div class="field-hint">Select one or more media types first; available locations depend on this choice.</div>
          </section>

          <section class="scope-field location-field" aria-labelledby="locations-label">
            <div id="locations-label" class="field-label">
              <v-icon small>place</v-icon>
              <span>2. Locations</span>
            </div>
            <div class="location-box" :class="{ 'location-box-disabled': !draft.mediaTypes.length }">
              <span class="selected-count">{{ draft.stationIds.length + missingStationIds.length }} selected</span>
              <v-btn
                outlined
                small
                color="info"
                :disabled="!draft.mediaTypes.length || busy"
                :aria-expanded="locationEditorOpen"
                aria-controls="location-editor"
                @click="locationEditorOpen = !locationEditorOpen"
              >
                <v-icon left small>edit_location</v-icon>
                {{ locationEditorOpen ? 'Hide locations' : 'Choose locations' }}
              </v-btn>
            </div>
            <div v-if="locationEditorOpen" id="location-editor" class="location-editor">
              <div class="search-tools">
                <v-text-field
                  v-model="locationSearch"
                  :disabled="!draft.mediaTypes.length || busy"
                  label="Search locations"
                  prepend-inner-icon="search"
                  clearable
                  outlined
                  dense
                  hide-details
                />
                <div class="list-controls">
                  <v-btn flat class="tool-text-btn" :disabled="busy || !matchingLocations.length" @click="selectMatchingLocations">
                    <v-icon left small>playlist_add_check</v-icon>
                    Select matching
                  </v-btn>
                  <v-btn flat class="tool-text-btn" :disabled="busy || !draft.stationIds.length" @click="clearLocations">
                    <v-icon left small>delete_outline</v-icon>
                    Clear selection
                  </v-btn>
                  <span class="list-result-count">{{ filteredLocationItems.length }} locations</span>
                  <span class="selection-count">{{ draft.stationIds.length }} selected</span>
                </div>
              </div>
              <div v-if="filteredLocationItems.length" class="location-options">
                <v-checkbox
                  v-for="location in filteredLocationItems"
                  :key="location.id"
                  :input-value="draft.stationIds.includes(location.id)"
                  :label="location.name"
                  :disabled="busy || (!location.available && !draft.stationIds.includes(location.id))"
                  hide-details
                  class="location-checkbox"
                  @change="toggleLocation(location.id, $event)"
                />
              </div>
              <div v-else class="empty-state">
                {{ draft.mediaTypes.length ? 'No locations match this search.' : 'Select media types to see available locations.' }}
              </div>
            </div>
            <div v-else class="field-hint">
              {{ draft.mediaTypes.length ? 'Choose locations to include in this query.' : 'Select media types first to enable location selection.' }}
            </div>
            <v-alert v-if="unavailableLocations.length" :value="true" type="warning" outline class="mt-2">
              {{ unavailableLocations.length }} selected location(s) do not match this media selection.
              <span>{{ conciseNames(unavailableLocations.map(location => location.name), 3) }}</span>
            </v-alert>
            <v-alert v-if="missingStationIds.length" :value="true" type="warning" outline class="mt-2">
              {{ missingStationIds.length }} saved location(s) are no longer available:
              {{ conciseNames(missingStationIds.map(id => `Location ${id}`), 3) }}.
              <v-btn outlined small @click="discardMissingLocations">Remove unavailable locations</v-btn>
            </v-alert>
          </section>
        </div>
        <div v-if="scopeError" class="error--text caption mt-2" role="alert">{{ scopeError }}</div>

        <div class="step-summary" aria-live="polite">
          <v-icon small>place</v-icon>
          <span>{{ scopeSummary }}</span>
        </div>
        <div class="step-action">
          <v-btn
            depressed
            class="step-complete-btn"
            :class="{ 'step-complete-btn-active': steps.scope }"
            :loading="scopeBusy"
            :disabled="!isInitialized || busy"
            @click="applyScope"
          >
            <v-icon left>{{ stepActionIcon(steps.scope) }}</v-icon>
            {{ stepActionLabel(steps.scope) }}
          </v-btn>
          <span class="action-explanation">Confirms the scope and refreshes available analytes.</span>
        </div>
      </v-card-text>
      </div>
    </v-card>

    <v-card id="query-step-2" class="wizard-step" :class="{ 'step-completed': steps.analytes, 'step-locked': !steps.scope }" flat>
      <div class="step-rail">
        <span class="step-number" :class="{ 'step-number-done': steps.analytes }">2</span>
      </div>
      <div class="wizard-step-main">
      <v-card-title class="headline">
        <v-icon class="step-heading-icon">science</v-icon>
        Choose analytes
        <span class="step-status">{{ steps.analytes ? 'Required · applied' : 'Required' }}</span>
      </v-card-title>
      <v-card-text>
        <p class="wizard-description">Choose at least one analyte. Selections stay visible when they are unavailable for the current scope.</p>
        <div class="analyte-toolbar">
          <div class="selection-count">
            <strong>{{ draft.analyteNames.length }}</strong> selected
            <span aria-hidden="true">·</span>
            <span :class="{ 'unavailable-summary': unavailableAnalytes.length }">
              {{ unavailableAnalytes.length ? `${unavailableAnalytes.length} unavailable for this scope` : 'All selected analytes are available' }}
            </span>
          </div>
        </div>
        <div class="analyte-picker">
          <section class="analyte-panel available-panel" aria-labelledby="available-analytes-title">
            <div class="analyte-panel-heading">
              <div class="analyte-panel-title">
                <strong id="available-analytes-title"><v-icon small>science</v-icon>Available analytes</strong>
                <span>{{ matchingAnalytes.length }} analytes</span>
              </div>
              <div class="analyte-panel-actions tool-actions">
                <v-btn flat class="tool-text-btn" :disabled="busy || !steps.scope || !matchingAnalytes.length" @click="selectMatchingAnalytes">
                  <v-icon left small>playlist_add_check</v-icon>
                  Select matching
                </v-btn>
                <v-btn flat class="tool-text-btn" :disabled="busy || !draft.analyteNames.length" @click="clearAnalytes">
                  <v-icon left small>delete_outline</v-icon>
                  Clear selection
                </v-btn>
              </div>
            </div>
            <v-text-field
              v-model="availableAnalyteSearch"
              :disabled="busy || !steps.scope"
              label="Search by EnviroData analyte name"
              prepend-inner-icon="search"
              clearable
              outlined
              dense
              hide-details
            />
            <div class="analyte-list-scroll">
              <div class="list-heading">
                <span>EnviroData analyte</span>
                <span>Availability</span>
              </div>
              <v-list v-if="matchingAnalytes.length" dense class="analyte-list">
                <v-list-tile
                  v-for="analyte in matchingAnalytes"
                  :key="analyte.AnalyteName"
                  :class="{ 'selected-analyte': isAnalyteSelected(analyte.AnalyteName), 'unavailable-row': analyte.available === false }"
                  @click="toggleAnalyte(analyte.AnalyteName)"
                >
                  <v-list-tile-action>
                    <v-checkbox
                      :input-value="isAnalyteSelected(analyte.AnalyteName)"
                      :disabled="busy || !steps.scope"
                      color="primary"
                      hide-details
                      @click.stop="toggleAnalyte(analyte.AnalyteName)"
                    />
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ analyteLabel(analyte) }}</v-list-tile-title>
                    <v-list-tile-sub-title class="analyte-meta">
                      <template v-if="analyteDisplayName(analyte)">{{ analyteDisplayName(analyte) }} · </template>{{ analyteDateRange(analyte) }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <span v-if="analyte.available === false" class="unavailable-label">Unavailable</span>
                  <span v-else class="available-label">Available</span>
                </v-list-tile>
              </v-list>
              <div v-else class="empty-state">
                {{ steps.scope ? 'No available analytes match this search.' : 'Complete the data scope to load available analytes.' }}
              </div>
            </div>
          </section>

          <section class="analyte-panel selected-panel" aria-labelledby="selected-analytes-title">
            <div class="analyte-panel-heading">
              <div class="analyte-panel-title">
                <strong id="selected-analytes-title"><v-icon small>check_circle</v-icon>Selected analytes</strong>
                <span>{{ draft.analyteNames.length }} selected</span>
              </div>
              <span v-if="unavailableAnalytes.length" class="selection-count unavailable-summary">
                {{ unavailableAnalytes.length }} unavailable
              </span>
            </div>
            <v-text-field
              v-model="selectedAnalyteSearch"
              :disabled="busy"
              label="Find in selection"
              prepend-inner-icon="search"
              clearable
              outlined
              dense
              hide-details
            />
            <v-list v-if="matchingSelectedAnalytes.length" dense class="analyte-list">
              <v-list-tile v-for="analyte in matchingSelectedAnalytes" :key="analyte.AnalyteName">
                <v-list-tile-content>
                  <v-list-tile-title>{{ analyteLabel(analyte) }}</v-list-tile-title>
                  <v-list-tile-sub-title :class="isAnalyteAvailable(analyte.AnalyteName) ? 'analyte-meta' : 'analyte-meta unavailable-summary'">
                    <template v-if="analyteDisplayName(analyte)">{{ analyteDisplayName(analyte) }} · </template>{{ analyteDateRange(analyte) }} ·
                    {{ isAnalyteAvailable(analyte.AnalyteName) ? 'Available for this scope' : 'Unavailable for this scope' }}
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-btn icon :aria-label="`Remove ${analyteLabel(analyte)}`" :disabled="busy" @click="removeAnalyte(analyte.AnalyteName)">
                    <v-icon>close</v-icon>
                  </v-btn>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
            <div v-else class="empty-state">
              {{ draft.analyteNames.length ? 'No selected analytes match this search.' : 'No analytes selected yet. Choose from the available list.' }}
            </div>
          </section>
        </div>
        <div v-if="analyteError" class="error--text caption mt-2" role="alert">{{ analyteError }}</div>
        <div class="step-summary" aria-live="polite">
          <v-icon small>science</v-icon>
          <span>{{ analyteSummary }}</span>
        </div>
        <div class="step-action">
          <v-btn
            depressed
            class="step-complete-btn"
            :class="{ 'step-complete-btn-active': steps.analytes }"
            :disabled="busy || !steps.scope"
            @click="applyAnalytes"
          >
            <v-icon left>{{ stepActionIcon(steps.analytes) }}</v-icon>
            {{ stepActionLabel(steps.analytes) }}
          </v-btn>
          <span class="action-explanation">Confirms the required analyte selection.</span>
        </div>
      </v-card-text>
      </div>
    </v-card>

    <v-card id="query-step-3" class="wizard-step" :class="{ 'step-completed': steps.dates, 'step-locked': !steps.analytes }" flat>
      <div class="step-rail">
        <span class="step-number" :class="{ 'step-number-done': steps.dates }">3</span>
      </div>
      <div class="wizard-step-main">
      <v-card-title class="headline">
        <v-icon class="step-heading-icon">date_range</v-icon>
        Choose the date range
        <span class="step-status">{{ steps.dates ? 'Required · applied' : 'Required' }}</span>
      </v-card-title>
      <v-card-text>
        <p class="wizard-description">Analyte availability for this scope is now known, so the period you pick is limited to dates that hold data for the selected analytes.</p>
        <div class="date-field">
          <DateRange
            ref="dateRange"
            :disabled="!isInitialized || busy || !steps.analytes"
            :availableDateRange="availableDateRange"
            @dateRange="onDateRangeChange"
          />
        </div>
        <div v-if="dateError" class="error--text caption mt-2" role="alert">{{ dateError }}</div>
        <div class="step-summary" aria-live="polite">
          <v-icon small>date_range</v-icon>
          <span>{{ dateSummary }}</span>
        </div>
        <div class="step-action">
          <v-btn
            depressed
            class="step-complete-btn"
            :class="{ 'step-complete-btn-active': steps.dates }"
            :disabled="busy || !steps.analytes"
            @click="applyDateRange"
          >
            <v-icon left>{{ stepActionIcon(steps.dates) }}</v-icon>
            {{ stepActionLabel(steps.dates) }}
          </v-btn>
          <span class="action-explanation">Confirms the reporting period for this extract.</span>
        </div>
      </v-card-text>
      </div>
    </v-card>

    <v-card id="query-step-4" class="wizard-step" :class="{ 'step-completed': steps.guidelines, 'step-locked': !steps.dates }" flat>
      <div class="step-rail">
        <span class="step-number" :class="{ 'step-number-done': steps.guidelines, 'step-number-optional': !steps.guidelines }">4</span>
      </div>
      <div class="wizard-step-main">
      <v-card-title class="headline">
        <v-icon class="step-heading-icon">gavel</v-icon>
        Add guidelines
        <span class="step-status optional-step-status">{{ steps.guidelines ? 'Optional · applied' : 'Optional · decide' }}</span>
      </v-card-title>
      <v-card-text>
        <p class="wizard-description">Guidelines are optional. Finish this step even if you do not need any.</p>
        <div class="guideline-toolbar">
          <v-btn-toggle v-model="guidelineFilter" mandatory class="guideline-tabs">
            <v-btn flat small :value="'available'"><v-icon left small>gavel</v-icon>Available</v-btn>
            <v-btn flat small :value="'selected'"><v-icon left small>check_circle</v-icon>Selected ({{ draft.guidelineIds.length }})</v-btn>
          </v-btn-toggle>
          <v-text-field
            v-model="guidelineSearch"
            :disabled="busy || !steps.dates"
            label="Search guidelines"
            prepend-inner-icon="search"
            clearable
            outlined
            dense
            hide-details
          />
          <v-btn outlined small :disabled="busy || !draft.guidelineIds.length" @click="clearGuidelines">
            <v-icon left small>clear_all</v-icon>
            Clear selection
          </v-btn>
          <span class="guideline-limit"><strong>{{ draft.guidelineIds.length }}</strong> / {{ maxGuidelines }} selected</span>
        </div>
        <div v-if="draft.guidelineIds.length >= maxGuidelines" class="warning--text caption mt-2">
          Maximum of {{ maxGuidelines }} guidelines selected.
        </div>
        <v-alert v-if="missingGuidelineIds.length" :value="true" type="warning" outline class="mt-3">
          {{ missingGuidelineIds.length }} saved guideline(s) are no longer available.
          <v-btn outlined small @click="discardMissingGuidelines">Remove unavailable saved guidelines</v-btn>
        </v-alert>
        <div v-if="guidelinesByCategory.length" class="guideline-list">
          <div v-for="category in guidelinesByCategory" :key="category.name">
            <v-list-tile class="guideline-category">
              <v-list-tile-action v-if="guidelineFilter === 'available'">
                <v-checkbox
                  :input-value="category.allSelected"
                  :indeterminate="category.partiallySelected"
                  :disabled="busy || !steps.dates || (!category.canSelect && !category.allSelected)"
                  color="primary"
                  hide-details
                  :aria-label="`Select all ${category.name} guidelines`"
                  @change="toggleGuidelineCategory(category)"
                />
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title><v-icon small class="mr-2">gavel</v-icon>{{ category.name }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-action-text>{{ category.guidelines.length }}</v-list-tile-action-text>
            </v-list-tile>
            <v-list-tile v-for="guideline in category.guidelines" :key="guideline.Id" class="guideline-row">
              <v-list-tile-action>
                <v-checkbox
                  :input-value="isGuidelineSelected(guideline.Id)"
                  :disabled="busy || !steps.dates || (!isGuidelineSelected(guideline.Id) && draft.guidelineIds.length >= maxGuidelines)"
                  color="primary"
                  hide-details
                  :aria-label="`${isGuidelineSelected(guideline.Id) ? 'Remove' : 'Add'} ${guideline.GuidelineName}`"
                  @change="toggleGuideline(guideline.Id)"
                />
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ guideline.GuidelineName }}</v-list-tile-title>
                <v-list-tile-sub-title>
                  {{ guideline.GuidelineLongName }}
                  <span v-if="!isGuidelineAvailable(guideline.Id)" class="warning--text"> · Unavailable for this scope</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </div>
        </div>
        <div v-else class="empty-state">
          {{ guidelineFilter === 'selected' ? 'No guidelines selected.' : 'No guidelines are available for this scope.' }}
        </div>
        <div v-if="guidelineError" class="error--text caption mt-2" role="alert">{{ guidelineError }}</div>
        <div class="step-summary" aria-live="polite">
          <v-icon small>gavel</v-icon>
          <span>{{ guidelineSummary }}</span>
        </div>
        <div class="step-action">
          <v-btn
            depressed
            class="step-complete-btn"
            :class="{ 'step-complete-btn-active': steps.guidelines }"
            :disabled="busy || !steps.dates"
            @click="applyGuidelines"
          >
            <v-icon left>{{ stepActionIcon(steps.guidelines) }}</v-icon>
            {{ stepActionLabel(steps.guidelines) }}
          </v-btn>
          <span class="action-explanation">Choose guidelines or continue without them.</span>
        </div>
      </v-card-text>
      </div>
    </v-card>

    <v-card id="query-step-5" class="wizard-step" :class="{ 'step-completed': canSubmit, 'step-locked': !steps.guidelines }" flat>
      <div class="step-rail">
        <span class="step-number" :class="{ 'step-number-done': canSubmit }">5</span>
      </div>
      <div class="wizard-step-main">
      <v-card-title class="headline">
        <v-icon class="step-heading-icon">assignment</v-icon>
        Review your selection
        <span class="step-status" :class="{ 'optional-step-status': !canSubmit }">{{ canSubmit ? 'Ready' : 'Waiting for Step 4' }}</span>
      </v-card-title>
      <v-card-text>
        <div class="review-grid">
          <div class="review-item">
            <span class="review-label">Media type</span>
            <span class="review-value">{{ conciseNames(draft.mediaTypes, 4) }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">Locations</span>
            <span class="review-value">{{ locationSummary }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">Date range</span>
            <span class="review-value">{{ dateRangeSummary }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">Selected analytes</span>
            <span class="review-value">{{ draft.analyteNames.length }} selected · {{ unavailableAnalytes.length }} unavailable</span>
          </div>
          <div class="review-item">
            <span class="review-label">Guidelines</span>
            <span class="review-value">{{ draft.guidelineIds.length ? `${draft.guidelineIds.length} selected · ${missingGuidelineIds.length} unavailable` : 'None · optional' }}</span>
          </div>
          <div class="review-item">
            <span class="review-label">Filter status</span>
            <span class="review-value">{{ canSubmit ? 'Applied' : 'Changes not applied' }}</span>
          </div>
        </div>
        <div class="health-message" :class="{ good: canSubmit && !unavailableAnalytes.length }">
          <span class="health-dot">{{ canSubmit && !unavailableAnalytes.length ? '✓' : '!' }}</span>
          <span>{{ reviewHealthMessage }}</span>
        </div>

        <v-divider class="my-3" />
        <h3 class="subheading font-weight-medium">Report options</h3>
        <ReportParams ref="reportParams" :disabled="busy" :selection="selection" @update:reportOptions="onReportOptions" />
        <div class="review-actions">
          <div class="review-actions-left">
            <v-btn outlined :disabled="busy || !hasDraftChanges" @click="resetChanges">
              <v-icon left small>refresh</v-icon>
              Reset changes
            </v-btn>
            <span class="review-note">{{ hasDraftChanges ? 'Changes are not applied yet.' : 'Selection is up to date.' }}</span>
          </div>
          <div class="review-actions-right">
            <AsyncDownload
              :disabled="!canSubmit"
              :selection="selection"
              :reportOptions="reportOptions"
              @loading="onDownloadLoading"
            />
          </div>
        </div>
      </v-card-text>
      </div>
    </v-card>
  </div>
</template>

<script>
  import moment from 'moment'
  import { createNamespacedHelpers } from 'vuex'
  import DateRange from './Parameters/DateRange/DateRange'
  import ReportParams from './Parameters/ReportParams'
  import AsyncDownload from './Actions/Download/AsyncDownload'
  import { analytesDateRange, StationMediaTypeGrouping } from '@/helpers/multiStationQuery'
  import { SOURCE_TYPES } from '@/store/MonitoringFeature/monitoringfeature'

  const datasource = createNamespacedHelpers('datasource')
  const MAX_GUIDELINES = 15

  const emptyDraft = () => ({
    mediaTypes: [],
    stationIds: [],
    dateRange: { start: null, end: null },
    analyteNames: [],
    guidelineIds: [],
  })

  const cloneDraft = draft => ({
    mediaTypes: [...draft.mediaTypes],
    stationIds: [...draft.stationIds],
    dateRange: { ...draft.dateRange },
    analyteNames: [...draft.analyteNames],
    guidelineIds: [...draft.guidelineIds],
  })

  const errorText = error => error && (
    error.displayMessage ||
    error.reasonPhrase ||
    error.Message ||
    error.message ||
    String(error)
  )

  export default {
    components: {
      DateRange,
      ReportParams,
      AsyncDownload,
    },
    props: {
      isGeneratingReport: Boolean,
    },
    data() {
      return {
        steps: {
          scope: false,
          analytes: false,
          dates: false,
          guidelines: false,
        },
        draft: emptyDraft(),
        lastAppliedDraft: emptyDraft(),
        hasAppliedSnapshot: false,
        hasRestorableSnapshot: false,
        dirtySinceApplied: false,
        activeStep: 1,
        allMediaTypes: [],
        guidelines: [],
        stationsMediaTypes: new StationMediaTypeGrouping(),
        allAnalytes: [],
        missingStationIds: [],
        missingGuidelineIds: [],
        reportOptions: null,
        locationSearch: '',
        locationEditorOpen: false,
        availableAnalyteSearch: '',
        selectedAnalyteSearch: '',
        guidelineSearch: '',
        guidelineFilter: 'available',
        isInitialized: false,
        scopeBusy: false,
        initializationError: '',
        errorMessage: '',
        scopeError: '',
        analyteError: '',
        dateError: '',
        guidelineError: '',
        dateRange: { start: null, end: null },
        availableDateRange: { start: null, end: null },
        maxGuidelines: MAX_GUIDELINES,
        lastAppliedReportOptions: null,
        initializationPromise: null,
      }
    },
    computed: {
      ...datasource.mapGetters(['dataSources']),
      locations() {
        const source = this.dataSources && this.dataSources[SOURCE_TYPES.discreteEnvirodata]
        if (!source) return []

        return source.map(location => {
          const medias = this.stationsMediaTypes.get(location.Id)
          return {
            id: location.Id,
            name: location.PrimaryName,
            category: location.LocationType,
            medias,
            available: this.locationMatchesMedia(medias),
          }
        })
      },
      locationById() {
        return this.locations.reduce((result, location) => {
          result[location.id] = location
          return result
        }, {})
      },
      matchingLocations() {
        const search = (this.locationSearch || '').toLowerCase().trim()
        return this.locations.filter(location =>
          location.available && (!search || location.name.toLowerCase().includes(search)))
      },
      locationItems() {
        const available = this.locations.filter(location => location.available)
        const unavailableSelected = this.draft.stationIds
          .map(id => this.locationById[id])
          .filter(location => location && !location.available)
        return [...available, ...unavailableSelected]
      },
      filteredLocationItems() {
        const search = (this.locationSearch || '').toLowerCase().trim()
        return this.locationItems.filter(location =>
          !search || location.name.toLowerCase().includes(search))
      },
      unavailableLocations() {
        return this.draft.stationIds
          .map(id => this.locationById[id] || { id, name: `Location ${id}`, available: false })
          .filter(location => !location.available)
      },
      availableAnalyteIndex() {
        return this.allAnalytes.reduce((result, analyte) => {
          result[analyte.AnalyteName] = analyte
          return result
        }, {})
      },
      availableAnalytes() {
        return this.allAnalytes
      },
      matchingAnalytes() {
        const available = this.filterAnalytes(this.availableAnalytes, this.availableAnalyteSearch)
          .map(analyte => ({ ...analyte, available: true }))
        const unavailableSelected = this.filterAnalytes(
          this.unavailableAnalytes.map(name => ({ AnalyteName: name, DisplayName: name, available: false })),
          this.availableAnalyteSearch
        )
        return [...available, ...unavailableSelected]
      },
      selectedAnalytes() {
        return this.draft.analyteNames.map(name =>
          this.availableAnalyteIndex[name] || { AnalyteName: name, DisplayName: name })
      },
      matchingSelectedAnalytes() {
        return this.filterAnalytes(this.selectedAnalytes, this.selectedAnalyteSearch)
      },
      unavailableAnalytes() {
        return this.draft.analyteNames.filter(name => !this.isAnalyteAvailable(name))
      },
      availableGuidelines() {
        const selectedMedia = new Set(this.selectedLocationMedia)
        return this.guidelines.filter(guideline =>
          guideline.MediaTypes && [...guideline.MediaTypes].some(media => selectedMedia.has(media)))
      },
      selectedLocationMedia() {
        const media = new Set()
        this.draft.stationIds.forEach(id => {
          const location = this.locationById[id]
          if (!location || !location.available) return
          location.medias.forEach(value => {
            if (this.draft.mediaTypes.includes(value)) media.add(value)
          })
        })
        return [...media]
      },
      guidelinesByCategory() {
        const selectedFilter = this.guidelineFilter === 'selected'
        const search = (this.guidelineSearch || '').toLowerCase().trim()
        const source = selectedFilter
          ? this.guidelines.filter(guideline => this.isGuidelineSelected(guideline.Id))
          : this.availableGuidelines
        const categories = {}

        source.forEach(guideline => {
          const matches = !search ||
            guideline.GuidelineName.toLowerCase().includes(search) ||
            (guideline.GuidelineLongName || '').toLowerCase().includes(search) ||
            (guideline.Category || '').toLowerCase().includes(search)
          if (!matches) return
          if (!categories[guideline.Category]) categories[guideline.Category] = []
          categories[guideline.Category].push(guideline)
        })

        return Object.keys(categories).sort().map(name => {
          const values = categories[name]
          const selectedCount = values.filter(value => this.isGuidelineSelected(value.Id)).length
          return {
            name,
            guidelines: values,
            allSelected: values.length > 0 && selectedCount === values.length,
            partiallySelected: selectedCount > 0 && selectedCount < values.length,
            canSelect: selectedCount === values.length || this.draft.guidelineIds.length < MAX_GUIDELINES,
          }
        })
      },
      stepNavItems() {
        return [
          { id: 1, label: 'Data scope', icon: 'place' },
          { id: 2, label: 'Analytes', icon: 'science' },
          { id: 3, label: 'Date range', icon: 'date_range' },
          { id: 4, label: 'Guidelines · optional', icon: 'gavel' },
          { id: 5, label: 'Review & download', icon: 'assignment' },
        ]
      },
      stateBannerTitle() {
        if (this.scopeBusy) return 'Refreshing availability'
        if (!this.steps.scope) return 'Complete the data scope'
        if (!this.steps.analytes) return 'Choose required analytes'
        if (!this.steps.dates) return 'Choose the date range'
        if (!this.steps.guidelines) return 'Complete optional guidelines'
        return 'Filters applied'
      },
      stateBannerCopy() {
        if (this.scopeBusy) return 'Loading the analytes and guidelines available for this scope.'
        if (!this.steps.scope) return 'Choose at least one media type and one location, then finish Step 1.'
        if (!this.steps.analytes) return 'Select one or more analytes to complete Step 2.'
        if (!this.steps.dates) return 'Confirm the reporting period to complete Step 3.'
        if (!this.steps.guidelines) return 'Finish Step 4 to apply this selection — guidelines are optional.'
        return 'The selections shown below are ready for review and download.'
      },
      reviewHealthMessage() {
        if (this.unavailableAnalytes.length) {
          const count = this.unavailableAnalytes.length
          return `${count} selected ${count === 1 ? 'analyte is' : 'analytes are'} unavailable for this scope. ${count === 1 ? 'It remains' : 'They remain'} selected and will be clearly identified.`
        }
        if (!this.canSubmit) return this.statusMessage
        return 'All required selections are applied and ready for download.'
      },
      selection() {
        return this.makeSelection(this.draft)
      },
      busy() {
        return this.scopeBusy || this.isGeneratingReport
      },
      canSubmit() {
        return this.steps.scope && this.steps.analytes && this.steps.dates && this.steps.guidelines && !this.busy
      },
      isDirty() {
        return this.dirtySinceApplied
      },
      hasDraftChanges() {
        const optionsChanged = this.hasAppliedSnapshot &&
          JSON.stringify(this.reportOptions) !== JSON.stringify(this.lastAppliedReportOptions)
        return this.isDirty || !this.sameDraft(this.draft, this.lastAppliedDraft) || optionsChanged
      },
      statusMessage() {
        if (this.scopeBusy) return 'Refreshing availability for this scope…'
        if (!this.steps.scope) return 'Complete the data scope before applying this selection.'
        if (!this.steps.analytes) return 'Analyte changes are not applied yet.'
        if (!this.steps.dates) return 'The date range is not applied yet.'
        if (!this.steps.guidelines) return 'Finish the optional guidelines step to apply this selection.'
        return 'Changes are not applied.'
      },
      scopeSummary() {
        const media = this.conciseNames(this.draft.mediaTypes, 3)
        const locationCount = this.draft.stationIds.length + this.missingStationIds.length
        const locations = locationCount
          ? `${locationCount} ${locationCount === 1 ? 'location' : 'locations'}`
          : 'no locations'
        return `${media} · ${locations}`
      },
      analyteSummary() {
        const count = this.draft.analyteNames.length
        if (!count) return 'No analytes selected · choose at least one'
        const names = this.conciseNames(this.draft.analyteNames.map(name =>
          (this.availableAnalyteIndex[name] || {}).AnalyteName || name), 4)
        return `${count} selected · ${this.unavailableAnalytes.length} unavailable · ${names}`
      },
      guidelineSummary() {
        return this.draft.guidelineIds.length
          ? `${this.draft.guidelineIds.length} selected · ${this.conciseNames(this.selectedGuidelineNames, 4)}`
          : 'No guidelines selected · optional'
      },
      selectedGuidelineNames() {
        return this.draft.guidelineIds.map(id => {
          const guideline = this.guidelines.find(item => String(item.Id) === String(id))
          return guideline ? guideline.GuidelineName : `Guideline ${id}`
        })
      },
      locationSummary() {
        const names = this.draft.stationIds.map(id =>
          (this.locationById[id] || {}).name || `Location ${id}`)
        names.push(...this.missingStationIds.map(id => `Location ${id} (unavailable)`))
        return names.length ? `${names.length} selected · ${this.conciseNames(names, 4)}` : 'None selected'
      },
      dateRangeSummary() {
        const { start, end } = this.draft.dateRange
        if (!start || !end) return 'All available data'
        return `${moment(start).format('D MMM YYYY')} – ${moment(end).format('D MMM YYYY')}`
      },
      dateSummary() {
        const available = this.availableDateRange
        const availableText = available.start && available.end
          ? `${moment(available.start).format('D MMM YYYY')} – ${moment(available.end).format('D MMM YYYY')}`
          : ''
        const { start, end } = this.draft.dateRange
        if (!start || !end) {
          return availableText
            ? `All available data · ${availableText}`
            : 'All available data'
        }
        const days = moment(end).diff(moment(start), 'days') + 1
        return `${this.dateRangeSummary} · ${days} ${days === 1 ? 'day' : 'days'}${availableText ? ` · data available ${availableText}` : ''}`
      },
    },
    watch: {
      canSubmit(value) {
        this.$emit('update:canSubmit', value)
      },
      hasDraftChanges(value) {
        this.$emit('update:dirty', value)
      },
    },
    methods: {
      conciseNames(names, max) {
        if (!names || !names.length) return 'None selected'
        const shown = names.slice(0, max)
        const remainder = names.length - shown.length
        return `${shown.join(', ')}${remainder > 0 ? ` +${remainder}` : ''}`
      },
      filterAnalytes(analytes, search) {
        const query = (search || '').toLowerCase().trim()
        if (!query) return analytes
        return analytes.filter(analyte =>
          (analyte.DisplayName || '').toLowerCase().includes(query) ||
          (analyte.AnalyteName || '').toLowerCase().includes(query))
      },
      analyteLabel(analyte) {
        if (!analyte) return ''
        return analyte.AnalyteName || analyte.DisplayName || ''
      },
      analyteDisplayName(analyte) {
        const displayName = analyte && analyte.DisplayName
        return displayName && displayName !== analyte.AnalyteName ? displayName : ''
      },
      formatAnalyteDate(value) {
        if (!value) return ''
        const date = moment(value)
        return date.isValid() ? date.format('YYYY-MM-DD') : ''
      },
      /**
       * The analyte payload from the single step-1 request already carries the first and
       * last sample dates, so the per-analyte range costs no extra call.
       */
      analyteDateRange(analyte) {
        const start = this.formatAnalyteDate(analyte && analyte.FirstSampleDate)
        const end = this.formatAnalyteDate(analyte && analyte.LastSampleDate)
        if (!start && !end) return 'no samples in scope'
        if (!start) return `up to ${end}`
        if (!end) return `${start} onwards`
        return `${start} – ${end}`
      },
      locationMatchesMedia(medias) {
        return !!(this.draft.mediaTypes.length && medias.some(media => this.draft.mediaTypes.includes(media)))
      },
      isAnalyteAvailable(name) {
        return !!this.availableAnalyteIndex[name]
      },
      isAnalyteSelected(name) {
        return this.draft.analyteNames.includes(name)
      },
      isGuidelineSelected(id) {
        return this.draft.guidelineIds.some(selectedId => String(selectedId) === String(id))
      },
      isGuidelineAvailable(id) {
        return this.availableGuidelines.some(guideline => String(guideline.Id) === String(id))
      },
      sameDraft(first, second) {
        return JSON.stringify(first) === JSON.stringify(second)
      },
      stepActionLabel(complete) {
        return complete ? 'Completed' : 'Complete'
      },
      stepActionIcon(complete) {
        return complete ? 'check_circle' : 'check'
      },
      isStepComplete(id) {
        if (id === 1) return this.steps.scope
        if (id === 2) return this.steps.analytes
        if (id === 3) return this.steps.dates
        if (id === 4) return this.steps.guidelines
        return this.canSubmit
      },
      canNavigateTo(id) {
        return id === 1 ||
          (id === 2 && this.steps.scope) ||
          (id === 3 && this.steps.scope && this.steps.analytes) ||
          (id === 4 && this.steps.scope && this.steps.analytes && this.steps.dates) ||
          (id === 5 && this.canSubmit)
      },
      goToStep(id) {
        if (!this.canNavigateTo(id)) return
        this.activeStep = id
        const section = this.$el.querySelector(`#query-step-${id}`)
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      invalidateFrom(step) {
        this.dirtySinceApplied = true
        if (step <= 1) {
          this.steps.scope = false
          this.steps.analytes = false
          this.steps.dates = false
          this.steps.guidelines = false
          this.scopeError = ''
          this.analyteError = ''
          this.dateError = ''
          this.guidelineError = ''
        } else if (step === 2) {
          this.steps.analytes = false
          this.steps.dates = false
          this.steps.guidelines = false
          this.analyteError = ''
          this.dateError = ''
          this.guidelineError = ''
        } else if (step === 3) {
          this.steps.dates = false
          this.steps.guidelines = false
          this.dateError = ''
          this.guidelineError = ''
        } else {
          this.steps.guidelines = false
          this.guidelineError = ''
        }
        this.$emit('update:dirty', true)
        this.$emit('update:canSubmit', false)
      },
      scopeChanged() {
        if (!this.isInitialized || this.scopeBusy) return
        this.invalidateFrom(1)
      },
      onMediaTypesChange(value) {
        if (this.busy) return
        this.draft.mediaTypes = [...new Set(value || [])]
        this.scopeChanged()
        // A Vuetify `multiple` v-select never closes its own menu after a pick
        // (VSelect only clears isMenuActive in the single-select branch), so close
        // it here to keep the media dropdown a pick-then-dismiss control.
        this.$nextTick(() => {
          const mediaSelect = this.$refs.mediaSelect
          if (mediaSelect) mediaSelect.isMenuActive = false
        })
      },
      toggleLocation(id, checked) {
        if (this.busy) return
        const selected = new Set(this.draft.stationIds)
        if (checked) {
          if (!this.locationById[id] || !this.locationById[id].available) return
          selected.add(id)
        } else {
          selected.delete(id)
        }
        this.draft.stationIds = [...selected]
        this.scopeChanged()
      },
      onDateRangeChange(value) {
        const next = value || { start: null, end: null }
        const changed = !this.sameDraft(next, this.draft.dateRange)
        // The picker also emits on mount and when a saved range is restored, so the
        // draft is always synced but only real user edits clear the completion state.
        this.draft.dateRange = next
        if (!changed || !this.isInitialized || this.scopeBusy) return
        this.invalidateFrom(3)
      },
      selectMatchingLocations() {
        const ids = this.matchingLocations.map(location => location.id)
        this.draft.stationIds = [...new Set([...this.draft.stationIds, ...ids])]
        this.scopeChanged()
      },
      clearLocations() {
        this.draft.stationIds = []
        this.scopeChanged()
      },
      discardMissingLocations() {
        this.missingStationIds = []
        this.$store.dispatch('multistation/updateMissingParams', { stations: [] })
        this.scopeChanged()
      },
      selectMatchingAnalytes() {
        if (!this.steps.scope) return
        const names = this.matchingAnalytes
          .filter(analyte => analyte.available !== false)
          .map(analyte => analyte.AnalyteName)
        this.draft.analyteNames = [...new Set([...this.draft.analyteNames, ...names])]
        this.invalidateFrom(2)
      },
      clearAnalytes() {
        this.draft.analyteNames = []
        this.invalidateFrom(2)
      },
      clearGuidelines() {
        this.draft.guidelineIds = []
        this.missingGuidelineIds = []
        this.$store.dispatch('multistation/updateMissingParams', { guidelines: [] })
        this.invalidateFrom(4)
      },
      discardMissingGuidelines() {
        const missing = new Set(this.missingGuidelineIds.map(id => String(id)))
        this.draft.guidelineIds = this.draft.guidelineIds.filter(id => !missing.has(String(id)))
        this.missingGuidelineIds = []
        this.$store.dispatch('multistation/updateMissingParams', { guidelines: [] })
        this.invalidateFrom(4)
      },
      toggleAnalyte(name) {
        if (!this.steps.scope || this.busy) return
        if (this.isAnalyteSelected(name)) {
          this.removeAnalyte(name)
        } else {
          this.draft.analyteNames = [...this.draft.analyteNames, name]
          this.invalidateFrom(2)
        }
      },
      removeAnalyte(name) {
        this.draft.analyteNames = this.draft.analyteNames.filter(value => value !== name)
        this.invalidateFrom(2)
      },
      toggleGuideline(id) {
        if (!this.steps.dates || this.busy) return
        if (this.isGuidelineSelected(id)) {
          this.draft.guidelineIds = this.draft.guidelineIds.filter(value => String(value) !== String(id))
        } else if (this.draft.guidelineIds.length < MAX_GUIDELINES) {
          this.draft.guidelineIds = [...this.draft.guidelineIds, id]
        }
        this.invalidateFrom(4)
      },
      toggleGuidelineCategory(category) {
        const allSelected = category.allSelected
        if (allSelected) {
          const categoryIds = new Set(category.guidelines.map(guideline => String(guideline.Id)))
          this.draft.guidelineIds = this.draft.guidelineIds.filter(id => !categoryIds.has(String(id)))
        } else {
          const current = [...this.draft.guidelineIds]
          category.guidelines.forEach(guideline => {
            if (current.length < MAX_GUIDELINES && !current.some(id => String(id) === String(guideline.Id))) {
              current.push(guideline.Id)
            }
          })
          this.draft.guidelineIds = current
        }
        this.invalidateFrom(4)
      },
      validateScope() {
        if (!this.draft.mediaTypes.length) return 'Select at least one media type.'
        if (!this.draft.stationIds.length) return 'Select at least one location.'
        if (this.unavailableLocations.length) return 'Remove locations unavailable for the selected media, or select a matching location.'
        return ''
      },
      validateDateRange() {
        const { start, end } = this.draft.dateRange
        if (!start && !end) return ''
        if (!start || !end) return 'Choose both a start date and an end date, or use All available data.'
        if (!moment(start).isValid() || !moment(end).isValid()) return 'Choose both a valid start date and end date.'
        if (moment(start).isAfter(moment(end), 'day')) return 'The start date must be on or before the end date.'
        return ''
      },
      async applyScope() {
        this.scopeError = this.validateScope()
        if (this.scopeError || this.scopeBusy) return

        this.scopeBusy = true
        this.$emit('update:loading', { selection: true })
        try {
          // The date range is chosen in step 3, once availability for the selected
          // analytes is known, so this first call runs unrestricted.
          const response = await this.$store.dispatch('multistation/fetchStationsAnalytes', {
            dateRange: {},
            stations: [...this.draft.stationIds],
            mediaTypes: [...this.draft.mediaTypes],
          })
          this.allAnalytes = Object.values(response)
          this.availableDateRange = analytesDateRange(this.allAnalytes)
          this.steps.scope = true
          this.steps.analytes = false
          this.steps.dates = false
          this.steps.guidelines = false
          this.updateMissingAnalytes()
          this.commitCurrentSelection()
          this.activeStep = 2
        } catch (error) {
          this.scopeError = `Could not refresh analyte availability: ${errorText(error)}`
        } finally {
          this.scopeBusy = false
          this.$emit('update:loading', { selection: false })
        }
      },
      selectedAnalytesDateRange() {
        const selected = analytesDateRange(this.selectedAnalytes)
        if (selected.start && selected.end) return selected
        return analytesDateRange(this.allAnalytes)
      },
      applyAnalytes() {
        this.analyteError = this.draft.analyteNames.length
          ? ''
          : 'Select at least one analyte before completing this step.'
        if (this.analyteError) return
        this.steps.analytes = true
        this.steps.dates = false
        this.steps.guidelines = false
        // Step 3 can only offer dates that hold data for the analytes just chosen.
        this.availableDateRange = this.selectedAnalytesDateRange()
        this.resetDateRangeIfUnavailable()
        this.updateMissingAnalytes()
        this.commitCurrentSelection()
        this.activeStep = 3
      },
      resetDateRangeIfUnavailable() {
        const available = this.availableDateRange
        const { start, end } = this.draft.dateRange
        if (!start || !end || !available.start || !available.end) return
        if (!moment(start).isBefore(available.start, 'day') && !moment(end).isAfter(available.end, 'day')) return
        // The analytes just chosen do not hold data across the whole current range,
        // so fall back to "All available data" for them rather than failing validation.
        this.draft.dateRange = { start: null, end: null }
        if (this.$refs.dateRange) this.$refs.dateRange.selectDateRange(this.draft.dateRange)
      },
      applyDateRange() {
        this.dateError = this.validateDateRange()
        if (this.dateError) return
        this.steps.dates = true
        this.steps.guidelines = false
        this.commitCurrentSelection()
        this.activeStep = 4
      },
      applyGuidelines() {
        if (this.draft.guidelineIds.length > MAX_GUIDELINES) {
          this.guidelineError = `Select no more than ${MAX_GUIDELINES} guidelines.`
          return
        }
        this.guidelineError = ''
        this.steps.guidelines = true
        this.missingGuidelineIds = this.draft.guidelineIds.filter(id =>
          !this.guidelines.some(guideline => String(guideline.Id) === String(id)))
        this.$store.dispatch('multistation/updateMissingParams', {
          guidelines: this.missingGuidelineIds,
        })
        this.commitCurrentSelection()
        this.lastAppliedDraft = cloneDraft(this.draft)
        this.lastAppliedReportOptions = this.reportOptions
          ? JSON.parse(JSON.stringify(this.reportOptions))
          : null
        this.hasAppliedSnapshot = true
        this.hasRestorableSnapshot = true
        this.dirtySinceApplied = false
        this.activeStep = 5
      },
      updateMissingAnalytes() {
        const missing = this.unavailableAnalytes
        this.$store.dispatch('multistation/updateMissingParams', { analytes: missing })
      },
      makeSelection(draft) {
        const stations = draft.stationIds.map(id => this.locationById[id]).filter(Boolean).map(location => ({
          id: location.id,
          name: location.name,
          medias: location.medias.filter(media => draft.mediaTypes.includes(media)),
        }))
        const analytes = draft.analyteNames.map(name =>
          this.availableAnalyteIndex[name] || { AnalyteName: name, DisplayName: name, StationIds: new Set() })
        const guidelines = draft.guidelineIds
          .map(id => this.guidelines.find(guideline => String(guideline.Id) === String(id)))
          .filter(Boolean)
        const stationMediaTypes = [...new Set(stations.reduce((result, station) => result.concat(station.medias), []))]
        const guidelinesMediaTypes = [...new Set(guidelines.reduce((result, guideline) =>
          result.concat([...guideline.MediaTypes]), []))]

        return {
          stations,
          analytes,
          guidelines,
          dateRange: { ...draft.dateRange },
          mediaTypes: [...draft.mediaTypes],
          stationsMediaTypes: stationMediaTypes,
          guidelinesMediaTypes,
        }
      },
      commitCurrentSelection() {
        this.$emit('update:selection', this.selection)
        this.$emit('update:canSubmit', this.canSubmit)
        this.$emit('update:dirty', this.hasDraftChanges)
      },
      onReportOptions(options) {
        this.reportOptions = options
        this.$emit('update:reportOptions', options)
        this.$emit('update:dirty', this.hasDraftChanges)
      },
      onDownloadLoading(value) {
        this.$emit('update:downloadLoading', value)
      },
      async resetChanges() {
        const target = cloneDraft(this.lastAppliedDraft)
        this.draft = target
        this.scopeError = ''
        this.analyteError = ''
        this.guidelineError = ''
        if (this.$refs.dateRange) this.$refs.dateRange.selectDateRange(target.dateRange)
        if (this.$refs.reportParams) {
          const options = this.hasRestorableSnapshot ? this.lastAppliedReportOptions : null
          this.$refs.reportParams.loadReportOptions(options)
        }

        this.dirtySinceApplied = false
        if (this.hasRestorableSnapshot) {
          this.scopeBusy = true
          this.$emit('update:loading', { selection: true })
          try {
            if (target.stationIds.length) {
              const response = await this.$store.dispatch('multistation/fetchStationsAnalytes', {
                dateRange: target.dateRange,
                stations: [...target.stationIds],
                mediaTypes: [...target.mediaTypes],
              })
              this.allAnalytes = Object.values(response)
            } else {
              this.allAnalytes = []
              this.availableDateRange = { start: null, end: null }
            }
            this.availableDateRange = analytesDateRange(this.allAnalytes)
            const hasValidDates = !!(target.dateRange.start && target.dateRange.end)
            this.steps.scope = target.mediaTypes.length > 0 &&
              target.stationIds.length > 0 &&
              !this.missingStationIds.length &&
              !this.unavailableLocations.length
            this.steps.analytes = this.steps.scope && target.analyteNames.length > 0
            this.steps.dates = this.steps.analytes && hasValidDates
            this.steps.guidelines = this.steps.dates
            this.updateMissingAnalytes()
            this.dirtySinceApplied = false
            this.commitCurrentSelection()
          } catch (error) {
            this.errorMessage = `Could not restore the last applied scope: ${errorText(error)}`
            this.invalidateFrom(1)
          } finally {
            this.scopeBusy = false
            this.$emit('update:loading', { selection: false })
          }
        } else {
          this.steps = { scope: false, analytes: false, dates: false, guidelines: false }
          this.dirtySinceApplied = false
          this.allAnalytes = []
          this.availableDateRange = { start: null, end: null }
          this.$store.dispatch('multistation/updateMissingParams', {
            stations: [],
            analytes: [],
            guidelines: [],
          })
          this.missingStationIds = []
          this.missingGuidelineIds = []
          this.commitCurrentSelection()
        }
      },
      async initialize() {
        try {
          const response = await this.$store.dispatch('multistation/fetchStationsGuidelines')
          this.guidelines = response.Guidelines
          this.stationsMediaTypes = response.StationMediaTypes
          this.allMediaTypes = response.StationMediaTypes.allMediaTypes
        } catch (error) {
          this.initializationError = `Could not load media and guidelines: ${errorText(error)}`
        } finally {
          this.isInitialized = true
          this.$emit('update:allMediaTypes', this.allMediaTypes)
          this.$emit('update:isInitialized', true)
          this.$emit('update:loading', { selection: false })
          this.lastAppliedDraft = cloneDraft(this.draft)
          this.dirtySinceApplied = false
        }
      },
      async loadSelection({
        stationIds,
        analyteNames,
        guidelineIds,
        dateRange,
        mediaTypes,
      }, selectionOptions, reportOptions) {
        if (this.initializationPromise) await this.initializationPromise
        const previousState = {
          draft: cloneDraft(this.draft),
          steps: { ...this.steps },
          allAnalytes: this.allAnalytes,
          availableDateRange: this.availableDateRange,
          reportOptions: this.reportOptions,
          missingStationIds: [...this.missingStationIds],
          missingGuidelineIds: [...this.missingGuidelineIds],
          lastAppliedDraft: cloneDraft(this.lastAppliedDraft),
          lastAppliedReportOptions: this.lastAppliedReportOptions,
          hasAppliedSnapshot: this.hasAppliedSnapshot,
          hasRestorableSnapshot: this.hasRestorableSnapshot,
          dirtySinceApplied: this.dirtySinceApplied,
          activeStep: this.activeStep,
          missingParams: {
            stations: [...this.missingStationIds],
            analytes: [...this.unavailableAnalytes],
            guidelines: [...this.missingGuidelineIds],
          },
        }
        this.scopeBusy = true
        this.$emit('update:loading', { selection: true })
        this.errorMessage = ''
        const stationIdsInput = stationIds || []
        const analyteNamesInput = analyteNames || []
        const guidelineIdsInput = guidelineIds || []
        const resolvedMediaTypes = mediaTypes || []
        let restoredDateRange = dateRange || { start: null, end: null }
        const missingStations = stationIdsInput.filter(id => !this.locationById[id])
        const missingGuidelines = guidelineIdsInput.filter(id =>
          !this.guidelines.some(guideline => String(guideline.Id) === String(id)))

        this.draft = {
          mediaTypes: [...resolvedMediaTypes],
          stationIds: stationIdsInput.filter(id => this.locationById[id]),
          dateRange: { ...restoredDateRange },
          analyteNames: [...analyteNamesInput],
          guidelineIds: [...guidelineIdsInput],
        }
        this.missingStationIds = [...missingStations]
        this.missingGuidelineIds = [...missingGuidelines]
        if (selectionOptions && selectionOptions.relativeDate && this.$refs.dateRange) {
          this.$refs.dateRange.selectDateRange(restoredDateRange, selectionOptions)
        } else if (this.$refs.dateRange) {
          this.$refs.dateRange.selectDateRange(restoredDateRange)
        }
        await this.$nextTick()
        restoredDateRange = { ...this.draft.dateRange }

        try {
          if (!stationIdsInput.length) {
            throw new Error('This saved query has no locations. Select one before applying the query.')
          }
          const hasMediaTypes = resolvedMediaTypes.length > 0
          const response = hasMediaTypes
            ? await this.$store.dispatch('multistation/fetchStationsAnalytes', {
              dateRange: restoredDateRange,
              stations: [...stationIdsInput],
              mediaTypes: [...resolvedMediaTypes],
            })
            : {}
          this.allAnalytes = Object.values(response)
          this.availableDateRange = analytesDateRange(this.allAnalytes)
          if (!restoredDateRange.start || !restoredDateRange.end) {
            const availableRange = analytesDateRange(this.allAnalytes)
            if (availableRange.start && availableRange.end) {
              this.draft.dateRange = {
                start: availableRange.start.format('YYYY-MM-DDTHH:mm:ss'),
                end: availableRange.end.format('YYYY-MM-DDTHH:mm:ss'),
              }
              if (this.$refs.dateRange) this.$refs.dateRange.selectDateRange(this.draft.dateRange)
            }
          }

          this.$store.dispatch('multistation/updateMissingParams', {
            stations: missingStations,
            analytes: hasMediaTypes
              ? this.draft.analyteNames.filter(name => !this.isAnalyteAvailable(name))
              : [],
            guidelines: missingGuidelines,
          })
          const hasValidDates = !!(this.draft.dateRange.start && this.draft.dateRange.end)
          const canRestoreScope = !missingStations.length &&
            this.draft.stationIds.length > 0 &&
            this.draft.mediaTypes.length > 0 &&
            !this.unavailableLocations.length
          const canRestoreAnalytes = canRestoreScope && this.draft.analyteNames.length > 0
          const canRestoreDates = canRestoreAnalytes && hasValidDates
          const canRestoreGuidelines = canRestoreDates && this.draft.guidelineIds.length <= MAX_GUIDELINES
          const complete = canRestoreGuidelines
          if (reportOptions !== undefined) {
            if (this.$refs.reportParams) this.$refs.reportParams.loadReportOptions(reportOptions)
            this.reportOptions = this.$refs.reportParams
              ? JSON.parse(JSON.stringify(this.$refs.reportParams.reportOptions))
              : reportOptions
          }
          this.lastAppliedDraft = cloneDraft(this.draft)
          this.lastAppliedReportOptions = this.reportOptions
            ? JSON.parse(JSON.stringify(this.reportOptions))
            : null
          this.hasAppliedSnapshot = complete
          this.hasRestorableSnapshot = true
          this.steps = {
            scope: canRestoreScope,
            analytes: canRestoreAnalytes,
            dates: canRestoreDates,
            guidelines: canRestoreGuidelines,
          }
          this.dirtySinceApplied = false
          this.commitCurrentSelection()
          this.activeStep = complete ? 5 : canRestoreDates ? 4 : canRestoreAnalytes ? 3 : canRestoreScope ? 2 : 1
          return true
        } catch (error) {
          this.draft = previousState.draft
          this.steps = previousState.steps
          this.allAnalytes = previousState.allAnalytes
          this.availableDateRange = previousState.availableDateRange
          this.reportOptions = previousState.reportOptions
          if (this.$refs.reportParams) {
            this.$refs.reportParams.loadReportOptions(previousState.reportOptions)
            this.reportOptions = JSON.parse(JSON.stringify(this.$refs.reportParams.reportOptions))
          }
          this.missingStationIds = previousState.missingStationIds
          this.missingGuidelineIds = previousState.missingGuidelineIds
          this.lastAppliedDraft = previousState.lastAppliedDraft
          this.lastAppliedReportOptions = previousState.lastAppliedReportOptions
          this.hasAppliedSnapshot = previousState.hasAppliedSnapshot
          this.hasRestorableSnapshot = previousState.hasRestorableSnapshot
          this.dirtySinceApplied = previousState.dirtySinceApplied
          this.activeStep = previousState.activeStep
          await this.$store.dispatch('multistation/updateMissingParams', previousState.missingParams)
          this.errorMessage = `Could not restore the saved query: ${errorText(error)}`
          this.commitCurrentSelection()
          return false
        } finally {
          this.scopeBusy = false
          this.$emit('update:loading', { selection: false })
        }
      },
      loadReportOptions(options) {
        if (this.$refs.reportParams) this.$refs.reportParams.loadReportOptions(options)
      },
      async startNewQuery() {
        this.scopeBusy = true
        this.draft = emptyDraft()
        this.missingStationIds = []
        this.missingGuidelineIds = []
        this.lastAppliedDraft = cloneDraft(this.draft)
        this.lastAppliedReportOptions = null
        this.hasAppliedSnapshot = false
        this.hasRestorableSnapshot = false
        this.dirtySinceApplied = false
        this.steps = { scope: false, analytes: false, dates: false, guidelines: false }
        this.activeStep = 1
        this.allAnalytes = []
        this.availableDateRange = { start: null, end: null }
        this.errorMessage = ''
        this.scopeError = ''
        this.analyteError = ''
        this.dateError = ''
        this.guidelineError = ''
        this.$store.dispatch('multistation/updateMissingParams', {
          stations: [],
          analytes: [],
          guidelines: [],
        })
        if (this.$refs.dateRange) this.$refs.dateRange.selectDateRange(this.draft.dateRange)
        if (this.$refs.reportParams) this.$refs.reportParams.reset()
        await this.$nextTick()
        this.scopeBusy = false
        this.commitCurrentSelection()
      },
      markCurrentAsSaved() {
        this.lastAppliedDraft = cloneDraft(this.draft)
        this.lastAppliedReportOptions = this.reportOptions
          ? JSON.parse(JSON.stringify(this.reportOptions))
          : null
        this.hasAppliedSnapshot = this.canSubmit
        this.hasRestorableSnapshot = this.canSubmit
        this.dirtySinceApplied = false
      },
    },
    mounted() {
      this.initializationPromise = this.initialize()
    },
  }
</script>

<style scoped>
  .discrete-query-wizard {
    padding-bottom: 20px;
  }

  .wizard-nav {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    margin: 0 0 14px;
    overflow: hidden;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, .12);
    border-radius: 4px;
  }

  .wizard-nav .v-btn {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 9px;
    min-width: 0;
    height: auto;
    min-height: 48px;
    margin: 0 !important;
    padding: 11px 13px;
    border-right: 1px solid rgba(0, 0, 0, .12);
    border-radius: 0;
    color: #666;
    font-size: 13px;
    font-weight: 400;
    white-space: normal;
    text-transform: none;
  }

  .wizard-nav .v-btn:last-child {
    border-right: 0;
  }

  /* Locked steps keep Vuetify's non-interactive behaviour (pointer-events:none) but
     use the mockup's muted link colour instead of Vuetify's disabled grey. */
  .wizard-nav .v-btn.v-btn--disabled {
    color: #666 !important;
    opacity: 1;
  }

  .wizard-nav .v-btn.v-btn--disabled .v-icon {
    color: #666 !important;
  }

  .wizard-nav .v-btn:not(.v-btn--disabled):hover {
    background: #f8f8f8;
  }

  .guideline-tabs {
    display: flex;
    gap: 4px;
    border: 0;
    border-radius: 0;
    background: transparent;
    overflow: visible;
  }

  .guideline-tabs .v-btn {
    min-width: 0;
    min-height: 30px;
    padding: 5px 9px;
    border: 1px solid transparent !important;
    border-radius: 4px !important;
    background: transparent;
    color: #555;
    font-size: 12px;
    font-weight: 500;
    text-transform: none;
  }

  .guideline-tabs .v-btn--active {
    background: #333 !important;
    color: #fff !important;
  }

  .wizard-nav-index {
    display: inline-grid;
    width: 25px;
    height: 25px;
    flex: 0 0 25px;
    place-items: center;
    border: 1px solid #999;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;
  }

  .wizard-nav .v-btn .v-icon {
    color: #666;
  }

  .wizard-nav .v-btn.wizard-nav-done .v-icon {
    color: #3f7a33;
  }

  /* Completed steps get a green-tinted cell so completion reads at a glance. */
  .wizard-nav .v-btn.wizard-nav-done {
    color: #3f7a33 !important;
    background: #eef5eb;
    font-weight: 600;
  }

  /* Current wins over done, matching the mockup's source order. */
  .wizard-nav .v-btn.wizard-nav-current .v-icon {
    color: #222;
  }

  /* Current wins over done, matching the mockup's source order. */
  .wizard-nav .v-btn.wizard-nav-current {
    color: #222 !important;
    font-weight: 500;
  }

  .wizard-nav-done .wizard-nav-index {
    color: #fff;
    background: #3f7a33;
    border-color: #3f7a33;
    font-size: 13px;
  }

  /* Current wins over done, matching the mockup's source order. */
  .wizard-nav-current .wizard-nav-index {
    color: #fff;
    background: #333;
    border-color: #333;
  }

  .state-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0 0 14px;
    padding: 11px 14px;
    border: 1px solid rgba(82, 153, 67, .32);
    border-radius: 4px;
    background: #eef5eb;
  }

  .state-mark {
    display: grid;
    width: 19px;
    height: 19px;
    flex: 0 0 19px;
    place-items: center;
    border-radius: 50%;
    background: #529943;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
  }

  .state-title {
    color: #315e2b;
    font-weight: 500;
  }

  .state-copy {
    color: #3d5d38;
    font-size: 13px;
  }

  .state-banner-pending {
    border-color: #d9b86f;
    background: #fff7e5;
  }

  .state-banner-pending .state-mark {
    background: #986500;
  }

  .state-banner-pending .state-title {
    color: #654b1c;
  }

  .state-banner-pending .state-copy {
    color: #654b1c;
  }

  .wizard-step {
    display: grid;
    grid-template-columns: 54px minmax(0, 1fr);
    margin-bottom: 12px;
    overflow: visible;
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, .12);
    scroll-margin-top: 16px;
  }

  .step-rail {
    position: relative;
    display: flex;
    justify-content: center;
    padding-top: 20px;
    border-right: 1px solid rgba(0, 0, 0, .08);
    /* Vuetify rounds a v-card's first child to inherit the card radius; the rail
       only rounds the card's outer (left) corners. */
    border-radius: 4px 0 0 4px !important;
    background: #fafafa;
  }

  .wizard-step:not(#query-step-5) .step-rail::after {
    position: absolute;
    top: 49px;
    bottom: -13px;
    left: 26px;
    width: 1px;
    background: #c8c8c8;
    content: '';
  }

  .wizard-step.step-completed .step-rail {
    background: #f4f8f2;
  }

  /* The mockup dims a step that is still gated by an earlier step. */
  .wizard-step.step-locked {
    opacity: .65;
  }

  .wizard-step.step-locked .wizard-step-main {
    pointer-events: none;
  }

  .step-number {
    z-index: 1;
    display: grid;
    width: 27px;
    height: 27px;
    place-items: center;
    border: 1px solid #888;
    border-radius: 50%;
    background: #fff;
    font-size: 13px;
    font-weight: 600;
  }

  .step-number-done {
    color: #fff;
    background: #529943;
    border-color: #529943;
  }

  .step-number-optional {
    border-style: dashed;
    color: #555;
  }

  .wizard-step-main {
    min-width: 0;
  }

  .wizard-step .v-card__title {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    min-height: 0;
    padding: 17px 20px 0;
    font-size: 17px !important;
    font-weight: 500;
    line-height: 1.3;
  }

  .step-heading-icon {
    color: #555;
  }

  .step-status {
    margin-left: auto;
    color: #529943;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .optional-step-status {
    color: #666;
  }

  /* Mockup: an optional step's status turns green once it is applied. */
  .wizard-step.step-completed .optional-step-status {
    color: #529943;
  }

  .wizard-step .v-card__text {
    padding: 0 20px 18px;
  }

  .wizard-description {
    margin: 3px 0 12px;
    color: #666;
    font-size: 13px;
  }

  .analyte-toolbar,
  .guideline-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
  }

  .scope-fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 13px 16px;
  }

  .scope-field {
    min-width: 0;
  }

  .media-field {
    grid-column: 1 / -1;
    max-width: 480px;
  }

  .location-field {
    grid-column: 1 / -1;
  }

  .field-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 5px;
    color: #555;
    font-size: 12px;
    font-weight: 500;
  }

  .field-hint {
    margin-top: 4px;
    color: #666;
    font-size: 11px;
  }

  .media-select >>> .v-input__slot {
    min-height: 44px;
    padding: 0 10px;
  }

  .media-select >>> .v-select__selections {
    min-height: 36px;
    align-items: center;
    padding: 4px 0;
  }

  .media-select >>> .v-chip {
    margin: 2px 4px 2px 0;
  }

  .location-box {
    display: flex;
    min-height: 40px;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 5px 8px;
    border: 1px solid rgba(0, 0, 0, .28);
    border-radius: 4px;
    background: #fff;
  }

  .location-box-disabled {
    color: #666;
    background: #f3f3f3;
  }

  .selected-count {
    padding: 2px 6px;
    color: #555;
    font-size: 12px;
    font-weight: 500;
  }

  .location-editor {
    max-height: 330px;
    margin-top: 8px;
    overflow: auto;
    padding: 8px 10px;
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
    background: #fafafa;
  }

  .location-editor .search-tools {
    margin: 0 -10px;
    padding: 8px 10px 9px;
  }

  .location-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 14px;
  }

  .location-checkbox {
    min-height: 32px;
    margin: 0;
    padding: 0;
  }

  .location-checkbox .v-input__slot {
    margin-bottom: 0;
  }

  .date-field >>> .v-card {
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
    background: #fafafa;
  }

  .date-field >>> .v-card__title {
    display: none;
  }

  .date-field >>> .v-card__text {
    padding: 8px 10px;
  }

  .step-summary {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 14px;
    padding: 9px 11px;
    border: 1px solid rgba(82, 153, 67, .22);
    border-radius: 4px;
    background: #f5f9f3;
    color: #315e2b;
    font-size: 12px;
    font-weight: 500;
  }

  .step-summary .v-icon {
    margin-top: 1px;
    color: #529943;
  }

  .step-action {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 9px;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, .09);
  }

  .action-explanation {
    color: #666;
    font-size: 12px;
  }

  /* Step completion control: black while pending, green once the step is applied,
     matching the green used for completed wizard tabs. */
  .step-action .v-btn.step-complete-btn {
    flex: none;
    min-height: 40px;
    padding: 0 20px;
    border: 1px solid #333;
    border-radius: 4px;
    background: #333 !important;
    box-shadow: none;
    color: #fff !important;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
  }

  .step-action .v-btn.step-complete-btn .v-icon {
    color: inherit !important;
  }

  .step-action .v-btn.step-complete-btn:not(.v-btn--disabled):hover {
    border-color: #1f1f1f;
    background: #1f1f1f !important;
  }

  .step-action .v-btn.step-complete-btn.step-complete-btn-active {
    border-color: #3f7a33;
    background: #3f7a33 !important;
  }

  .step-action .v-btn.step-complete-btn.step-complete-btn-active:not(.v-btn--disabled):hover {
    border-color: #356a2a;
    background: #356a2a !important;
  }

  .step-action .v-btn.step-complete-btn.v-btn--disabled {
    border-color: #e0e0e0 !important;
    background: #f2f2f2 !important;
    color: rgba(0, 0, 0, .38) !important;
  }

  .guideline-limit {
    margin-left: auto;
    color: #666;
    font-size: 12px;
  }

  .guideline-limit strong {
    color: #333;
  }

  .selection-count {
    color: #666;
    font-size: 13px;
  }

  .selection-count strong {
    color: #333;
  }

  .analyte-picker {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(250px, .85fr);
    gap: 14px;
    align-items: start;
    margin-top: 8px;
  }

  .analyte-panel {
    min-width: 0;
  }

  .available-panel {
    grid-column: 1;
    grid-row: 1;
  }

  .selected-panel {
    position: sticky;
    top: 12px;
    grid-column: 2;
    grid-row: 1;
    padding: 10px;
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
    background: #fafafa;
  }

  .analyte-panel-heading {
    display: flex;
    min-height: 40px;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
  }

  .analyte-panel-title {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  .analyte-panel-heading strong {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
  }

  .analyte-panel-heading span {
    color: #666;
    font-size: 11px;
  }

  .analyte-panel-heading .unavailable-summary {
    color: #84252b;
    font-size: 11px;
  }

  .analyte-panel-actions,
  .tool-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 6px;
  }

  /* Text-style tool buttons, matching the mockup's `.btn.text.small`. */
  .tool-text-btn {
    min-width: 0;
    /* Vuetify's `.v-btn { height: 36px }` wins over min-height alone. */
    height: 30px;
    min-height: 30px;
    margin: 0;
    padding: 4px 9px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: transparent;
    box-shadow: none;
    color: #075fa8;
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
  }

  /* Vuetify forces `!important` grey on disabled buttons; the mockup keeps the link
     colour and dims the whole control instead (button:disabled { opacity: .48 }). */
  .tool-text-btn.v-btn--disabled {
    color: #075fa8 !important;
    opacity: .48;
  }

  .tool-text-btn:hover:not(.v-btn--disabled) {
    background: #edf6ff;
  }

  .tool-text-btn .v-icon {
    font-size: 16px;
  }

  .search-tools {
    position: sticky;
    top: -10px;
    z-index: 2;
    padding: 0 0 9px;
    background: #fafafa;
  }

  .list-controls {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
  }

  .list-result-count {
    padding: 4px 2px;
    color: #666;
    font-size: 11px;
  }

  .list-controls .selection-count {
    margin-left: auto;
  }

  .available-panel .list-heading {
    position: sticky;
    top: 0;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 9px;
    padding: 8px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, .14);
    background: #f3f3f3;
    color: #666;
    font-size: 12px;
    font-weight: 500;
  }

  .analyte-list-scroll {
    height: 360px;
    max-height: 48vh;
    overflow: auto;
    overscroll-behavior: contain;
    border: 1px solid rgba(0, 0, 0, .14);
    border-radius: 4px;
  }

  .analyte-list {
    padding: 0;
  }

  .available-panel .analyte-list {
    border: 0;
  }

  .selected-panel .analyte-list {
    min-height: 68px;
    max-height: 360px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, .14);
  }

  .analyte-list >>> .v-list__tile:not(.v-list__tile--avatar) {
    height: auto;
    min-height: 44px;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .analyte-list >>> .v-list__tile__content {
    height: auto;
    min-width: 0;
    overflow: visible;
  }

  .analyte-list >>> .v-list__tile__title {
    height: auto;
    line-height: 1.35;
  }

  .analyte-list >>> .v-list__tile__sub-title {
    height: auto;
    line-height: 1.35;
    white-space: normal;
  }

  .selected-analyte {
    background: rgba(76, 175, 80, .08);
  }

  .analyte-meta {
    color: rgba(0, 0, 0, .5);
    font-size: 11px;
  }

  .unavailable-summary {
    color: #84252b;
  }

  .unavailable-row {
    background: #fff4f4;
    color: #84252b;
  }

  .unavailable-label {
    flex: none;
    margin-left: auto;
    padding: 2px 8px;
    color: #84252b;
    background: #f8d9dc;
    border-radius: 12px;
    font-size: 11px;
    text-align: center;
    white-space: nowrap;
  }

  .available-label {
    flex: none;
    margin-left: auto;
    padding: 2px 8px;
    color: #315e2b;
    background: #e5f0e1;
    border-radius: 12px;
    font-size: 11px;
    text-align: center;
    white-space: nowrap;
  }

  .empty-state {
    padding: 16px;
    color: #666;
    text-align: center;
  }

  .guideline-list {
    max-height: 360px;
    margin-top: 12px;
    overflow-y: auto;
  }

  .guideline-category {
    background: #f5f5f5;
    font-weight: 500;
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0 18px;
    margin-top: 4px;
    border-top: 1px solid rgba(0, 0, 0, .08);
  }

  .review-item {
    display: flex;
    flex-direction: column;
    padding: 10px 0;
    border-bottom: 1px solid rgba(0, 0, 0, .08);
  }

  .review-label {
    display: block;
    margin-bottom: 2px;
    color: #666;
    font-size: 11px;
  }

  .review-value {
    font-weight: 500;
  }

  .health-message {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 12px;
    padding: 9px 11px;
    border-radius: 4px;
    background: #fff7e5;
    color: #654b1c;
    font-size: 12px;
  }

  .health-message.good {
    background: #eef5eb;
    color: #315e2b;
  }

  .health-dot {
    font-weight: 700;
  }

  .review-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
    padding-top: 13px;
    border-top: 1px solid rgba(0, 0, 0, .14);
  }

  .review-actions-left,
  .review-actions-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .review-note {
    color: #666;
    font-size: 12px;
  }

  @media (max-width: 760px) {
    .wizard-nav-label {
      display: none;
    }

    .wizard-nav .v-btn {
      justify-content: center;
      padding: 9px 4px;
    }

    .wizard-nav-index {
      margin-right: 0;
    }

    .analyte-picker {
      grid-template-columns: minmax(0, 1fr);
    }

    .selected-panel,
    .available-panel {
      position: static;
      grid-column: 1;
      grid-row: auto;
    }

    .available-panel {
      order: 1;
    }

    .selected-panel {
      order: 2;
    }

    .scope-fields {
      grid-template-columns: minmax(0, 1fr);
    }

    .media-field {
      grid-column: 1;
      max-width: none;
    }

    .review-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 0 12px;
    }

    .guideline-toolbar,
    .review-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .analyte-toolbar,
    .analyte-panel-heading {
      align-items: flex-start;
      flex-direction: column;
    }

    .analyte-toolbar .v-text-field {
      width: 100%;
    }

    .analyte-panel-actions {
      width: 100%;
      justify-content: flex-start;
    }

    .review-actions > * {
      width: 100%;
    }

    .wizard-step {
      grid-template-columns: 38px minmax(0, 1fr);
    }

    .step-rail {
      padding-top: 18px;
    }

    .wizard-step:not(#query-step-5) .step-rail::after {
      top: 46px;
      left: 18px;
    }

    .wizard-step .v-card__title {
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 6px;
      padding: 15px 13px 0;
      font-size: 16px !important;
    }

    .wizard-step .v-card__text {
      padding: 0 13px 15px;
    }

    .step-status {
      width: 100%;
      margin-left: 32px;
      font-size: 11px;
    }

    .analyte-list-scroll {
      height: 290px;
      max-height: 42vh;
    }

    .selected-panel .analyte-list {
      max-height: 190px;
    }

    .location-options {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  @media (max-width: 420px) {
    .wizard-nav .v-btn {
      justify-content: center;
      padding: 9px 4px;
      font-size: 11px;
    }

    .wizard-nav-index {
      width: 22px;
      height: 22px;
      flex: 0 0 22px;
    }

    .review-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .wizard-nav-label,
    .wizard-nav .v-icon {
      display: none;
    }

    .location-box {
      align-items: flex-start;
    }

    .location-options {
      grid-template-columns: minmax(0, 1fr);
    }

    .list-controls .selection-count {
      margin-left: 0;
    }
  }
</style>
